import type { ReactElement } from 'react'

import type { Poem } from '@maria-cavalheiro-website/shared-types'
import { PillButton } from '@maria-cavalheiro-website/shared-ui-web'

import { EbookLauncher } from '../../ebook/ebook-launcher.tsx'
import { PoemArticle } from '../../poems/poem-article.tsx'
import { listPoems } from '../../poems/poem-collection.ts'
import { poemNeighbours } from '../../poems/poem-navigation.ts'
import { PoemSubNav } from '../../poems/poem-sub-nav.tsx'
import { ReadingScaleControl } from '../../poems/reading-scale-control.tsx'
import { useIllustrations } from '../../poems/use-illustrations.ts'
import { useReadingScale } from '../../poems/use-reading-scale.ts'

export interface PoemPageProps {
  readonly poem: Poem
}

/** Matches the index's launcher; the book is one book wherever it is opened. */
const EBOOK_LABEL = 'Ler como livro'

/**
 * NAMES THE THING, NOT THE ACTION — "Ilustrações", never "Ocultar ilustrações".
 *
 * This is a toggle button, so `aria-pressed` carries the state and a screen reader
 * says it on every press. A label that ALSO changed with the state would have the
 * button announce "Mostrar ilustrações, não pressionado", which contradicts itself:
 * the reader cannot tell whether the words describe what is true now or what
 * pressing will do. The WAI-ARIA toggle-button pattern is a fixed name plus a
 * changing pressed state, and that is what this is.
 *
 * A VISIBLE LABEL AND NO `aria-label`, so § 2.5.3 Label in Name has nothing to
 * disagree about. Below 36em the pill clips it to its icon — clipped, not removed,
 * so the accessible name is the same at every width (see `_pill.sass`).
 */
const ILLUSTRATIONS_LABEL = 'Ilustrações'

/**
 * `secondary`, NOT the `toggle` VARIANT, and the icon carries the state instead.
 *
 * `toggle` says "released" by dropping its border. That reads correctly in the
 * index's mode group, where three pills sit side by side and a released one is
 * still read against its neighbours. Here the switch stands alone between the
 * scale steppers and the launcher, and below 36em its label is clipped — released,
 * it would be a bare glyph with no boundary, which stops looking like a control
 * exactly when the reader needs to find it again to bring the pictures back.
 *
 * So it keeps the launcher's shape in both states and the glyph changes. That also
 * survives a theme the pill's fill would not: `--bg-3` sits a hair off `--bg` in
 * the light palette, whereas a crossed-out picture is a crossed-out picture. The
 * icon is `aria-hidden` and says nothing twice — `aria-pressed` is what a screen
 * reader hears.
 */
const ILLUSTRATIONS_ICON = { shown: 'image', hidden: 'image-off' } as const

/**
 * Container for a single poem.
 *
 * Owns exactly one concern beyond composition: wiring the reading-scale hook to
 * its presenter. The scale is expressed as a `data-` attribute rather than an
 * inline `style`, because `style-src 'self'` admits no inline style attribute.
 *
 * The reading-scale control has no counterpart in the design. It stays: it is the
 * mechanism by which this route satisfies § 1.4.4 at AAA on a 320px viewport, and
 * three E2E tests including the 200% reflow test depend on it. It sits above the
 * article and reads quieter than the sub-nav, so the two bars do not stack up into
 * something that looks like a toolbar.
 *
 * `Escape` does NOT return to the index. With real routes that is a navigation,
 * not a dismissal, and hijacking `Escape` on a document route surprises keyboard
 * and screen-reader users who expect it to dismiss transient UI — of which there
 * is none here.
 */
export function PoemPage({ poem }: PoemPageProps): ReactElement {
  const { scale, canIncrease, canDecrease, increase, decrease } = useReadingScale()
  const illustrations = useIllustrations()
  const neighbours = poemNeighbours(poem)

  // Two `data-` attributes on the root, one reason: `style-src 'self'` admits no
  // inline style attribute, so every piece of state the stylesheet needs reaches
  // it as an attribute. `data-illustrations` carries the fade phase — see
  // `useIllustrations` for why "shown" and "restored" are not one value.
  return (
    <div className="poem-page" data-reading-scale={scale} data-illustrations={illustrations.phase}>
      {/* The illustrations used to be a fixed layer here, behind everything at
          z-index 0. Phase 0018 moved them into `PoemArticle`, where the plate
          opens the poem and each stanza carries its own figure — a picture that
          belongs to a stanza cannot be positioned by the page that contains it. */}
      {neighbours === undefined ? null : (
        <PoemSubNav previous={neighbours.previous} next={neighbours.next} />
      )}

      <div className="poem-page__column">
        {/* Three reading controls, one row, all three about comfort: the scale
            steps this page's type, the switch takes the pictures away from a
            reader who wants the verse alone, and the launcher hands the whole
            collection to one who would rather have the screen. */}
        <div className="poem-page__tools">
          <ReadingScaleControl
            scale={scale}
            canIncrease={canIncrease}
            canDecrease={canDecrease}
            onIncrease={increase}
            onDecrease={decrease}
          />

          <PillButton
            variant="secondary"
            icon={illustrations.visible ? ILLUSTRATIONS_ICON.shown : ILLUSTRATIONS_ICON.hidden}
            pressed={illustrations.visible}
            onClick={illustrations.toggle}
          >
            {ILLUSTRATIONS_LABEL}
          </PillButton>

          {/* Opens ON this poem rather than at page one — the reader is already
              here, and starting them somewhere else would be a navigation. */}
          <EbookLauncher poems={listPoems()} startSlug={poem.slug} label={EBOOK_LABEL} />
        </div>

        {/* `mounted`, not `visible`: through the fade out the reader has already
            said no and the button already reads released, but the figures must
            still be in the document for the stylesheet to have something to
            collapse. They leave one `--motion-duration` later. */}
        <PoemArticle poem={poem} headingLevel={1} showIllustrations={illustrations.mounted} />
      </div>
    </div>
  )
}
